import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
// Rxjs
import { Observable , of} from 'rxjs';
import { catchError, finalize, map } from 'rxjs/operators';
// Environment
import { environment } from 'src/environments/environment';
// Model
import { MessageType } from '../components/messages/message-handler/message-handler.component';
import { MessageHandlerService } from '../components/messages/message-handler/message-handler.service';
import { SaveType } from '../core/models/enumerations/save-type';
// Interface
import { TranslateService } from '@ngx-translate/core';
import { Cacheable, LocalStorageStrategy } from 'ts-cacheable';
import { LoadWheelService } from '../components/load-wheel/load-wheel.service';
import { News, RelatedEntityResult } from '../core/models/common';
import { EntityName } from '../core/models/enumerations/entity-name';
import { SpinnerProcess } from '../core/models/spinner-process';
import { TankSearch, TankSearchAdapter, TankType } from '../core/models/tank';
import { TanksDisplay } from '../core/models/tank/display/tanks-display';
import { RecordLockingFlow } from '../core/record-locking/record-locking-flow';
import { EntityCommonService } from '../core/services/entity-common.service';
import { LockSameUserMessageService, LockSameUserResult } from '../core/services/lock-same-user-message.service';
import { TelemetryService } from '../core/services/telemetry.service';
import { EntityUtilsService } from '../core/utils/entity-utils.service';

@Injectable({
    providedIn: 'root'
})
export class TankSectionService {

    constructor(
        private readonly recordLockingFlow: RecordLockingFlow,
        private readonly http: HttpClient,
        private readonly messageHandlerService: MessageHandlerService,
        private readonly loadWheelService: LoadWheelService,
        private readonly entityCommonService: EntityCommonService,
        private readonly translate: TranslateService,
        private readonly tankSearchAdapter: TankSearchAdapter,
        private readonly entityUtilsService: EntityUtilsService,
        private readonly lockSameUserMessageService: LockSameUserMessageService,
        private readonly telemetry: TelemetryService,
    ) { }

    getTankDetailsById(tankId: string, isReload: boolean = false, quickAccess: boolean = false): Promise<TanksDisplay> {
        let mainLockID: number = 0;

        if (isReload) {
            mainLockID = this.recordLockingFlow.getLockID(EntityName.TANK, tankId);
        }

        return new Promise<TanksDisplay>((resolve) => {
            this.lockSameUserMessageService.validateEntityIsLockedByUser(this.recordLockingFlow, EntityName.TANK, tankId, mainLockID, quickAccess).then((lockSameUserResult: LockSameUserResult) => {
                if(lockSameUserResult.openEntity) {
                    this.getEntityDetailsById(tankId, mainLockID, false).subscribe((entityDisplay: TanksDisplay) => {
                        resolve(entityDisplay);
                    });
                } else {
                    resolve(TanksDisplay.BuildTanksDisplay({}));
                }
            });
        });
    }

    private getEntityDetailsById(tankId: string, mainLockID: number, onlyForRead: boolean): Observable<TanksDisplay> {
        const wheel: SpinnerProcess = this.loadWheelService.showWheel(this.translate.instant("loading.tank") + ' (' + this.translate.instant("loading.mainRecord") + ')');
        return this.http.get<TanksDisplay>(`${environment.apiUrl}tank/${tankId}/${mainLockID}/${onlyForRead}`)
            .pipe(
                map((data: any) => {
                    let tankDisplay = TanksDisplay.BuildTanksDisplay({});
                    if (data.response) {
                        tankDisplay = TanksDisplay.BuildTanksDisplay(data.response);

                        if(onlyForRead){
                            if(tankDisplay.tankId && !tankDisplay.lockError){
                                this.recordLockingFlow.setLockItem(EntityName.TANK, tankDisplay.tankId, tankDisplay.lockId, tankDisplay.lockMode);
                                this.recordLockingFlow.clearLockFlow(EntityName.TANK, tankId);
                            }
                        } else if (tankDisplay.tankId) {
                            if (tankDisplay.hasDiffUserLockError()) {
                                this.messageHandlerService.show(tankDisplay.lockError, MessageType.INFO);
                            }
    
                            // Set the locking info into lock global list.
                            this.recordLockingFlow.setLockItem(EntityName.TANK, tankDisplay.tankId, tankDisplay.lockId, tankDisplay.lockMode);
                            this.recordLockingFlow.initLockRefreshTimer(EntityName.TANK, tankDisplay.tankId, tankDisplay.lockId);
                        } else {
                            this.messageHandlerService.show(tankDisplay.lockError, MessageType.ERROR);
                        }
                    } else {
                        const errorType = MessageHandlerService.errorType(data);
                        if (errorType === MessageType.INFO) {
                            this.entityCommonService.sendEntityNotFoundEvent();
                        } else {
                            this.messageHandlerService.show(MessageHandlerService.errorMessage(data), errorType);
                        }
                    }
                    return tankDisplay;
                }),
                catchError((error) => {
                    return of(TanksDisplay.BuildTanksDisplay({}));
                }),
                finalize(() => this.loadWheelService.hideWheel(wheel))
            );
    }

    getTankDetailsByIdForRead(tankId: string): Observable<TanksDisplay> {
        const wheel: SpinnerProcess = this.loadWheelService.showWheel(this.translate.instant("loading.tank") + ' (' + this.translate.instant("loading.mainRecord") + ')');
        return this.http.get<TanksDisplay>(`${environment.apiUrl}tank/getTankDetailsByIdForRead/${tankId}`)
            .pipe(
                map((data: any) => {
                    let tankDisplay = TanksDisplay.BuildTanksDisplay({});
                    if (data.response) {
                        tankDisplay = TanksDisplay.BuildTanksDisplay(data.response);
                    } else {
                        const errorType = MessageHandlerService.errorType(data);
                        if (errorType === MessageType.INFO) {
                            this.entityCommonService.sendEntityNotFoundEvent();
                        } else {
                            this.messageHandlerService.show(MessageHandlerService.errorMessage(data), errorType);
                        }
                    }
                    return tankDisplay;
                }),
                catchError((error) => {
                    return of(TanksDisplay.BuildTanksDisplay({}));
                }),
                finalize(() => this.loadWheelService.hideWheel(wheel))
            );
    }

    getTankById(tankId: number): Observable<TankSearch> {
        const wheel: SpinnerProcess = this.loadWheelService.showWheel(this.translate.instant("loading.tank") + ' (' + this.translate.instant("loading.details") + ')');
        return this.http.get<TankSearch>(`${environment.apiUrl}tank/${tankId}`)
            .pipe(
                map((data: any) => {
                    return this.tankSearchAdapter.adapt(data.response[0]);
                }),
                finalize(() => this.loadWheelService.hideWheel(wheel))
            );
    }

    saveTankDetails(tankDisplay: TanksDisplay): Observable<TanksDisplay> {
        tankDisplay = this.entityUtilsService.prepareEntityForSave(tankDisplay) as TanksDisplay;
        return this.http.put<TanksDisplay>(`${environment.apiUrl}tank`, tankDisplay)
            .pipe(
                map((data: any) => {
                    if (data.response === null) {
                        if (data.exception !== null) {
                            this.messageHandlerService.showExceptionDialog(data);
                        } else if (data.message !== null) {
                            if (tankDisplay.saveType === SaveType.TYPE_QC || tankDisplay.saveType === SaveType.UPDATE) {
                                //When the user attempts to QC a record while the lock was already cleared,
                                //this//the above warning message should still be thrown; however, the record should not reload.
                                if (data.messageType !== MessageType.WARNING.valueOf()) {
                                    this.entityCommonService.sendReloadEvent();
                                }
                            } else {
                                tankDisplay.intDataDepMigError = data.message;
                            }

                        }
                    } else {
                        const isQcUpdateOrAmend = tankDisplay.saveType === SaveType.TYPE_QC ||
                        tankDisplay.saveType === SaveType.AMENDMENT ||
                        tankDisplay.saveType === SaveType.UPDATE;

                        const afterClearFunc = () => this.entityCommonService.sendPreReleaseEvent();
                        this.recordLockingFlow.clearLockFlow(EntityName.TANK, String(tankDisplay.tankId), isQcUpdateOrAmend, afterClearFunc);
                    }
                    return data.response;
                })
            );
    }

    tankNextId(): Observable<TanksDisplay> {
        const wheel: SpinnerProcess = this.loadWheelService.showWheel(this.translate.instant("loading.nextId"));
        return this.http.get<any>(`${environment.apiUrl}tank/nextId`)
            .pipe(
                map((data: any) => {
                    const tankDisplay = TanksDisplay.BuildNewTank(data.response);
                    if (tankDisplay?.tankId) {
                        // Set the locking info into lock global list.
                        this.recordLockingFlow.setLockItem(EntityName.TANK, tankDisplay.tankId, -1);
                    }
                    return tankDisplay;
                }),
                finalize(() => this.loadWheelService.hideWheel(wheel))
            );
    }

    searchTanks(params: HttpParams): Observable<TankSearch[]> {
        const searchStart: number = performance.now();

        return this.http.get<TankSearch[]>(`${environment.apiUrl}tank/search`, { params: params })
            .pipe(
                map((data: any) => {
                    this.entityUtilsService.verifyMaxResult(data);
                    let tanks: Array<any> = data.response;
                    
                    this.telemetry.entitySearchTime("tank", performance.now() - searchStart);
                    
                    return tanks.map(item => this.tankSearchAdapter.adapt(item));
                })
            );
    }

    getNewsInfo(tankId: number): Observable<RelatedEntityResult<News>> {
        const wheel: SpinnerProcess = this.loadWheelService.showWheel(this.translate.instant("common.loading") + ' ' + this.translate.instant("relatedInfo.common.related") + ' ' + this.translate.instant("relatedInfo.news.title"));
        return this.http.get<RelatedEntityResult<News>>(`${environment.apiUrl}tank/news/${tankId}`)
            .pipe(
                map((data: any) => RelatedEntityResult.Build(data.response, News.BuildNew)),
                finalize(() => this.loadWheelService.hideWheel(wheel))
            );
    }
    
    @Cacheable({
        storageStrategy: LocalStorageStrategy
    })
    getTankTypes(): Observable<TankType[]> {
        const wheel: SpinnerProcess = this.loadWheelService.showWheel(this.translate.instant("common.loading") + ' ' + this.translate.instant("tank.searchTank.tankType"));
          return this.http.get<TankType>(`${environment.apiUrl}tank/tankTypes`)
              .pipe(
                  map((data: any) => {
                      return TankType.BuildTankType(data.response);
                  }),
                  finalize(() => this.loadWheelService.hideWheel(wheel))
              );
      }
}


